import React, { useState } from "react";
import PropTypes from "prop-types";
import Layout from "../Layout";
import axiosInstance from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const FacilityPreview = ({ setStep, formData = {} }) => {
  const navigate = useNavigate();

  const [visibility, setVisibility] = useState("Yes");
  const [aboutUs, setAboutUs] = useState(
    formData.facilityInformation?.aboutUs || ""
  );
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const facilityInformation = formData.facilityInformation || {
    facilityName: "",
    facilityContactInformation: {
      facilityContactNumber: "",
      facilityEmailId: "",
      websiteLink: "",
    },
    facilityAddressDetails: {
      addressLine1: "",
      addressLine2: "",
      pincode: "",
    },
    systemOfMedicineCode: [],
    facilityTypeCode: "",
    ownershipCode: "",
    facilityOperationalStatus: "",
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        facilityInformation: {
          ...formData.facilityInformation,
          aboutUs,
          publicVisibility: visibility === "Yes",
        },
      };
      console.log("Submitting payload:", payload);

      const response = await axiosInstance.post(
        "/doctor/hfr/createbasic-facility-info",
        payload
      );

      console.log("Response:", response.data);

      // ApiResponse wraps data in 'data' field
      const responseData = response.data.data || response.data;

      if (response.data.success || responseData.trackingId) {
        setSuccess(true);
        toast.success("Facility registered successfully! 🎉");

        // Redirect to dashboard after 1.5 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        toast.error(response.data.message || "Failed to submit data");
        setError(response.data.message || "Failed to submit data");
      }
    } catch (err) {
      console.error("Error submitting facility:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Error submitting data";
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className='min-h-screen bg-gray-50 px-8 py-10'>
        <div className='mb-10 flex justify-center'>
          <div className='flex items-center space-x-8'>
            <StepCircle label='1' text='Facility Registration Form' completed />
            <div className='w-16 border-t-2 border-blue-500' />
            <StepCircle
              label='2'
              text='Detailed Facility Information'
              completed
            />
            <div className='w-16 border-t-2 border-blue-500' />
            <StepCircle label='3' text='Preview Profile' active />
          </div>
        </div>

        {error && <div className='mb-4 text-sm text-red-500'>{error}</div>}

        <div className='mx-auto flex w-full max-w-6xl justify-between rounded-lg bg-white p-6 shadow'>
          <div className='w-[48%] rounded border p-6'>
            <p className='mb-6 text-center text-gray-500'>
              The below information is available for public display.
            </p>
            <div className='space-y-2 text-sm text-gray-700'>
              <InfoRow
                label='Facility Name'
                value={facilityInformation.facilityName}
              />
              <InfoRow label='Facility Manager' value='N/A' />
              <InfoRow
                label='Facility Mobile Number'
                value={facilityInformation.facilityContactInformation.facilityContactNumber.replace(
                  /(\d{4})\d{4}(\d{2})/,
                  "$1xxxx$2"
                )}
              />
              <InfoRow
                label='Facility Email'
                value={facilityInformation.facilityContactInformation.facilityEmailId.replace(
                  /^(.{2}).*?@(.+)/,
                  "$1***@$2"
                )}
              />
              <InfoRow
                label='Facility address'
                value={`${facilityInformation.facilityAddressDetails.addressLine1}, ${
                  facilityInformation.facilityAddressDetails.addressLine2
                }, ${facilityInformation.facilityAddressDetails.pincode}`}
              />
              <InfoRow
                label='System of medicine'
                value={
                  Array.isArray(facilityInformation.systemOfMedicineCode)
                    ? facilityInformation.systemOfMedicineCode
                        .map(
                          (code) =>
                            medicineOptions.find((opt) => opt.code === code)
                              ?.value || code
                        )
                        .join(", ")
                    : facilityInformation.systemOfMedicineCode || "N/A"
                }
              />
              <InfoRow
                label='Facility type'
                value={facilityInformation.facilityTypeCode}
              />
              <InfoRow
                label='Facility ownership'
                value={
                  ownershipOptions.find(
                    (opt) => opt.code === facilityInformation.ownershipCode
                  )?.value || facilityInformation.ownershipCode
                }
              />
              <InfoRow
                label='Facility status'
                value={facilityInformation.facilityOperationalStatus}
              />
              <InfoRow
                label='Facility website'
                value={
                  facilityInformation.facilityContactInformation.websiteLink
                }
              />
            </div>

            <button
              className='mt-6 rounded bg-gray-200 px-6 py-2'
              onClick={() => setStep(2)}
            >
              Back
            </button>
          </div>

          <div className='w-[48%] space-y-4'>
            <p className='text-sm'>
              To preview your details,{" "}
              <button
                type='button'
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className='text-blue-500 hover:underline'
              >
                Click here
              </button>
            </p>

            <p className='text-sm font-medium'>
              Your profile will be visible to the public?
            </p>
            <div className='mb-4 flex space-x-6'>
              <label className='flex items-center space-x-1'>
                <input
                  type='radio'
                  name='visibility'
                  value='Yes'
                  checked={visibility === "Yes"}
                  onChange={() => setVisibility("Yes")}
                />
                <span>Yes</span>
              </label>
              <label className='flex items-center space-x-1'>
                <input
                  type='radio'
                  name='visibility'
                  value='No'
                  checked={visibility === "No"}
                  onChange={() => setVisibility("No")}
                />
                <span>No</span>
              </label>
            </div>

            <label className='block text-sm font-medium'>About</label>
            <textarea
              className='h-40 w-full rounded border p-2'
              value={aboutUs}
              onChange={(e) => setAboutUs(e.target.value)}
            ></textarea>

            <div className='flex justify-end'>
              <button
                className='rounded bg-blue-500 px-6 py-2 text-white hover:bg-blue-600'
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Esign and submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const ownershipOptions = [
  { code: "G", value: "Government" },
  { code: "P", value: "Private" },
  { code: "PP", value: "Public Private Partnership" },
];

const medicineOptions = [
  { code: "UN", value: "Unani" },
  { code: "M", value: "Modern Medicine(Allopathy)" },
  { code: "YN", value: "Yoga and Naturopathy" },
  { code: "A", value: "Ayurveda" },
  { code: "SD", value: "Siddha" },
  { code: "H", value: "Homeopathy" },
  { code: "P", value: "Physiotherapy" },
  { code: "SR", value: "Sowa-Rigpa" },
  { code: "D", value: "Dentistry" },
];

const StepCircle = ({ label, text, active, completed }) => {
  const circleStyle = completed
    ? "bg-blue-500 text-white"
    : active
      ? "border-2 border-blue-500 text-blue-500"
      : "border-2 border-gray-300 text-gray-400";

  return (
    <div className='flex flex-col items-center space-y-1 text-xs'>
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-full ${circleStyle}`}
      >
        {label}
      </div>
      <div className='w-24 text-center'>{text}</div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className='flex justify-between'>
    <span className='text-gray-600'>{label}</span>
    <span>{value || "N/A"}</span>
  </div>
);

FacilityPreview.propTypes = {
  setStep: PropTypes.func.isRequired,
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
};

FacilityPreview.defaultProps = {
  formData: {},
};

export default FacilityPreview;
